import { useState, useEffect, useRef, Fragment } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import {
  Search,
  Rocket,
  BarChart2,
  Trophy,
  ArrowRight,
  ArrowDown,
  PartyPopper,
  Check,
  Sparkles as SparklesIcon,
  Star,
} from "lucide-react";
import ProcessStairs from "./ProcessStairs";

const DEFAULT_STEPS = [
  {
    icon: Search,
    num: "01",
    title: "Audit & Strategy",
    text: "Deep analysis of your app, market, competitors and channels to build a data-backed growth blueprint.",
    accent: "#2196F3",
  },
  {
    icon: Rocket,
    num: "02",
    title: "Launch & Execute",
    text: "ASO, creatives, paid campaigns, influencers and content — launched in parallel under one plan.",
    accent: "#a855f7",
  },
  {
    icon: BarChart2,
    num: "03",
    title: "Measure & Optimise",
    text: "Real-time KPI dashboards, A/B testing and weekly iterations driven by what the data shows.",
    accent: "#10b981",
  },
  {
    icon: Trophy,
    num: "04",
    title: "Scale & Win",
    text: "Double down on proven channels, expand to new markets and reach category leadership.",
    accent: "#f59e0b",
  },
];

const COLORS = [
  "#2196F3", "#a855f7", "#f59e0b", "#10b981", "#ef4444",
  "#ffffff", "#f472b6", "#34d399", "#fbbf24", "#60a5fa",
];

// First confetti burst — radial, fast
const BURST_1 = Array.from({ length: 40 }, (_, i) => {
  const angle = (i / 40) * Math.PI * 2;
  const radius = 80 + (i % 5) * 30;
  return {
    color: COLORS[i % COLORS.length],
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius,
    w: 5 + (i % 4) * 2,
    h: 3 + (i % 3) * 1.5,
    dur: 1.0 + (i % 7) * 0.13,
    rot: i % 2 === 0 ? 540 : -480,
    delay: (i % 8) * 0.02,
  };
});
// Second burst — wider, slower, different colors
const BURST_2 = Array.from({ length: 30 }, (_, i) => {
  const angle = (i / 30) * Math.PI * 2 + 0.3;
  const radius = 120 + (i % 4) * 40;
  return {
    color: COLORS[(i + 3) % COLORS.length],
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius - 20,
    w: 4 + (i % 3) * 2,
    h: 6 + (i % 4) * 2,
    dur: 1.6 + (i % 5) * 0.18,
    rot: i % 2 === 0 ? 720 : -600,
    delay: 0.3 + (i % 6) * 0.04,
  };
});
// Rain confetti — falls from top
const RAIN = Array.from({ length: 22 }, (_, i) => ({
  color: COLORS[(i + 5) % COLORS.length],
  x: (i / 21 - 0.5) * 280,
  startY: -100,
  endY: 320,
  w: 4 + (i % 3) * 2,
  h: 7 + (i % 4) * 2,
  dur: 1.4 + (i % 5) * 0.18,
  rot: i % 2 === 0 ? 720 : -720,
  delay: 0.4 + (i % 9) * 0.06,
}));
// Sparkles — twinkle around step 4 for longer
const SPARKLES = Array.from({ length: 14 }, (_, i) => ({
  x: (Math.cos((i / 14) * Math.PI * 2) * (70 + (i % 3) * 25)),
  y: (Math.sin((i / 14) * Math.PI * 2) * (70 + (i % 3) * 25)),
  size: 8 + (i % 3) * 4,
  delay: 0.6 + (i % 7) * 0.1,
  dur: 1.6 + (i % 4) * 0.2,
}));

// Floating background orbs for the dark section
const ORBS = [
  { left: "8%", top: "20%", size: 320, color: "#2196F3", duration: 14, delay: 0 },
  { left: "85%", top: "55%", size: 380, color: "#a855f7", duration: 18, delay: 3 },
  { left: "55%", top: "80%", size: 260, color: "#10b981", duration: 16, delay: 6 },
  { left: "30%", top: "65%", size: 220, color: "#f59e0b", duration: 12, delay: 1 },
];

function BackgroundOrbs() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {ORBS.map((o, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full blur-3xl"
          style={{
            left: o.left,
            top: o.top,
            width: o.size,
            height: o.size,
            backgroundColor: o.color,
            opacity: 0.08,
          }}
          animate={{
            x: [0, 40, -20, 0],
            y: [0, -30, 20, 0],
            scale: [1, 1.15, 0.95, 1],
          }}
          transition={{
            duration: o.duration,
            delay: o.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
}

// Animated arrow with comet trail and pulse rings
function ArrowConnector({ animate, direction = "horizontal", accentFrom, accentTo }) {
  const isH = direction === "horizontal";
  return (
    <div
      className={`flex items-center justify-center ${
        isH ? "hidden lg:flex w-full h-full" : "lg:hidden w-full py-4"
      }`}
    >
      <div
        className={`relative flex items-center justify-center ${
          isH ? "w-full" : "h-16 flex-col"
        }`}
      >
        {/* Static dim line */}
        <div className={`bg-white/10 ${isH ? "h-[2px] w-full" : "w-[2px] h-full"}`} />

        {/* Animated gradient line */}
        <motion.div
          className={`absolute ${
            isH
              ? "h-[2px] left-0 origin-left"
              : "w-[2px] top-0 origin-top"
          }`}
          style={{
            background: isH
              ? `linear-gradient(90deg, ${accentFrom}, ${accentTo})`
              : `linear-gradient(180deg, ${accentFrom}, ${accentTo})`,
            ...(isH ? { width: "100%" } : { height: "100%" }),
            boxShadow: animate ? `0 0 14px ${accentFrom}aa` : "none",
          }}
          initial={isH ? { scaleX: 0 } : { scaleY: 0 }}
          animate={
            animate
              ? isH
                ? { scaleX: 1 }
                : { scaleY: 1 }
              : isH
              ? { scaleX: 0 }
              : { scaleY: 0 }
          }
          transition={{ duration: 0.7, ease: [0.65, 0, 0.35, 1] }}
        />

        {/* Comet head — lead */}
        <motion.div
          className="absolute w-3.5 h-3.5 rounded-full"
          style={{
            backgroundColor: accentTo,
            boxShadow: `0 0 18px 4px ${accentTo}`,
          }}
          initial={
            isH
              ? { left: "0%", top: "50%", x: "-50%", y: "-50%", opacity: 0 }
              : { top: "0%", left: "50%", x: "-50%", y: "-50%", opacity: 0 }
          }
          animate={
            animate
              ? isH
                ? { left: "100%", opacity: [0, 1, 1, 0] }
                : { top: "100%", opacity: [0, 1, 1, 0] }
              : { opacity: 0 }
          }
          transition={{ duration: 0.7, ease: [0.65, 0, 0.35, 1] }}
        />

        {/* Comet trail — smaller dot following */}
        <motion.div
          className="absolute w-2 h-2 rounded-full"
          style={{
            backgroundColor: accentFrom,
            opacity: 0.7,
          }}
          initial={
            isH
              ? { left: "0%", top: "50%", x: "-50%", y: "-50%", opacity: 0 }
              : { top: "0%", left: "50%", x: "-50%", y: "-50%", opacity: 0 }
          }
          animate={
            animate
              ? isH
                ? { left: "100%", opacity: [0, 0.7, 0.7, 0] }
                : { top: "100%", opacity: [0, 0.7, 0.7, 0] }
              : { opacity: 0 }
          }
          transition={{ duration: 0.7, delay: 0.08, ease: [0.65, 0, 0.35, 1] }}
        />

        {/* Pulse ring at start */}
        {animate && (
          <motion.div
            className={`absolute rounded-full border-2 ${isH ? "left-0" : "top-0"}`}
            style={{
              borderColor: accentFrom,
              width: 12,
              height: 12,
              ...(isH
                ? { left: 0, top: "50%", x: "-50%", y: "-50%" }
                : { top: 0, left: "50%", x: "-50%", y: "-50%" }),
            }}
            initial={{ scale: 0.5, opacity: 1 }}
            animate={{ scale: 3, opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          />
        )}

        {/* Arrow head at end */}
        <motion.div
          className="absolute"
          style={
            isH
              ? { right: -3, top: "50%", transform: "translateY(-50%)" }
              : { bottom: -3, left: "50%", transform: "translateX(-50%)" }
          }
          initial={{ opacity: 0, scale: 0.4 }}
          animate={animate ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.4 }}
          transition={{ duration: 0.3, delay: 0.6, type: "spring", stiffness: 300 }}
        >
          {isH ? (
            <ArrowRight
              className="w-6 h-6"
              style={{ color: accentTo, filter: `drop-shadow(0 0 8px ${accentTo})` }}
              strokeWidth={2.5}
            />
          ) : (
            <ArrowDown
              className="w-6 h-6"
              style={{ color: accentTo, filter: `drop-shadow(0 0 8px ${accentTo})` }}
              strokeWidth={2.5}
            />
          )}
        </motion.div>
      </div>
    </div>
  );
}

// Multi-stage celebration filling the entire box
function Celebration({ show, accent }) {
  return (
    <AnimatePresence>
      {show && (
        <>
          {/* Background flash gradient */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: `radial-gradient(circle at 50% 50%, ${accent}66, transparent 70%)`,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0.4, 0] }}
            transition={{ duration: 1.6, ease: "easeOut" }}
          />

          {/* Pulsing edge glow */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 1, 0, 1, 0] }}
            transition={{ duration: 2, ease: "easeInOut" }}
            style={{
              boxShadow: `inset 0 0 60px ${accent}, inset 0 0 100px ${accent}66`,
            }}
          />

          {/* Burst 1 — radial fast */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
            {BURST_1.map((p, i) => (
              <motion.div
                key={`b1-${i}`}
                className="absolute rounded-sm"
                style={{
                  width: p.w,
                  height: p.h,
                  backgroundColor: p.color,
                  top: "50%",
                  left: "50%",
                  boxShadow: `0 0 8px ${p.color}`,
                }}
                initial={{ x: 0, y: 0, opacity: 1, rotate: 0, scale: 1 }}
                animate={{
                  x: p.x,
                  y: p.y,
                  opacity: 0,
                  rotate: p.rot,
                  scale: 0.3,
                }}
                transition={{ duration: p.dur, delay: p.delay, ease: "easeOut" }}
              />
            ))}
          </div>

          {/* Burst 2 — wider second wave */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
            {BURST_2.map((p, i) => (
              <motion.div
                key={`b2-${i}`}
                className="absolute rounded-sm"
                style={{
                  width: p.w,
                  height: p.h,
                  backgroundColor: p.color,
                  top: "50%",
                  left: "50%",
                  boxShadow: `0 0 6px ${p.color}`,
                }}
                initial={{ x: 0, y: 0, opacity: 0, rotate: 0, scale: 0.5 }}
                animate={{
                  x: p.x,
                  y: p.y,
                  opacity: [0, 1, 0],
                  rotate: p.rot,
                  scale: 0.4,
                }}
                transition={{ duration: p.dur, delay: p.delay, ease: "easeOut" }}
              />
            ))}
          </div>

          {/* Rain from top */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
            {RAIN.map((p, i) => (
              <motion.div
                key={`r-${i}`}
                className="absolute rounded-sm"
                style={{
                  width: p.w,
                  height: p.h,
                  backgroundColor: p.color,
                  top: 0,
                  left: "50%",
                }}
                initial={{ x: p.x, y: p.startY, opacity: 1, rotate: 0 }}
                animate={{
                  x: p.x + (i % 2 === 0 ? 30 : -30),
                  y: p.endY,
                  opacity: [1, 1, 0],
                  rotate: p.rot,
                }}
                transition={{ duration: p.dur, delay: p.delay, ease: "easeIn" }}
              />
            ))}
          </div>

          {/* Sparkles — twinkling stars */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden>
            {SPARKLES.map((s, i) => (
              <motion.div
                key={`s-${i}`}
                className="absolute"
                style={{
                  top: "50%",
                  left: "50%",
                  width: s.size,
                  height: s.size,
                  marginLeft: s.x,
                  marginTop: s.y,
                }}
                initial={{ opacity: 0, scale: 0, rotate: 0 }}
                animate={{
                  opacity: [0, 1, 0, 1, 0],
                  scale: [0, 1, 0.5, 1.2, 0],
                  rotate: 180,
                }}
                transition={{ duration: s.dur, delay: s.delay, ease: "easeInOut" }}
              >
                <Star
                  className="w-full h-full text-white"
                  fill="currentColor"
                  style={{ filter: `drop-shadow(0 0 6px ${accent})` }}
                />
              </motion.div>
            ))}
          </div>
        </>
      )}
    </AnimatePresence>
  );
}

// Step card
function StepCard({ step, idx, isActive, isCompleted, isLast, showConfetti, confettiBadgeLabel }) {
  const Icon = step.icon;
  const accent = step.accent;

  return (
    <motion.div
      className="relative h-full p-7 md:p-8 overflow-hidden flex flex-col group"
      animate={{
        scale: isActive && !isCompleted ? 1.025 : 1,
      }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      data-testid={`process-step-${idx + 1}`}
      style={{
        backgroundColor: "rgba(20,20,35,0.7)",
        backdropFilter: "blur(8px)",
      }}
    >
      {/* Animated gradient border (active state) */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          borderColor: isActive ? `${accent}aa` : "rgba(255,255,255,0.08)",
          boxShadow: isActive
            ? `0 0 0 1px ${accent}66, 0 0 30px -5px ${accent}aa, inset 0 0 30px ${accent}22`
            : "0 0 0 1px rgba(255,255,255,0.08)",
        }}
        transition={{ duration: 0.6 }}
        style={{
          borderWidth: 1,
          borderStyle: "solid",
        }}
      />

      {/* Giant number watermark */}
      <div
        className="absolute -top-4 -right-2 font-display font-black select-none pointer-events-none leading-none"
        style={{
          fontSize: "9rem",
          color: isActive ? `${accent}11` : "rgba(255,255,255,0.025)",
          transition: "color 0.6s",
        }}
      >
        {step.num}
      </div>

      {/* Activation pulse ring */}
      {isActive && !isCompleted && (
        <motion.div
          className="absolute inset-2 pointer-events-none"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: [0, 0.7, 0], scale: [0.95, 1.04, 1.06] }}
          transition={{ duration: 1.1, ease: "easeOut" }}
          style={{
            border: `2px solid ${accent}`,
            borderRadius: 0,
          }}
        />
      )}

      {/* Completed checkmark */}
      <AnimatePresence>
        {isCompleted && (
          <motion.div
            className="absolute top-4 right-4 w-7 h-7 rounded-full grid place-items-center z-20"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 280, damping: 16 }}
            style={{
              backgroundColor: accent,
              boxShadow: `0 0 14px ${accent}aa`,
            }}
          >
            <Check className="w-4 h-4 text-white" strokeWidth={3} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Number label (small, top) */}
      <motion.span
        className="font-display text-[11px] font-black uppercase tracking-[0.2em] block mb-7 relative z-10"
        animate={{
          color: isActive ? accent : "rgba(255,255,255,0.3)",
        }}
        transition={{ duration: 0.5 }}
      >
        Step {step.num}
      </motion.span>

      {/* Icon — animated badge */}
      <motion.div
        className="w-14 h-14 grid place-items-center mb-6 relative z-10"
        animate={{
          backgroundColor: isActive ? `${accent}22` : "rgba(255,255,255,0.04)",
          rotate: isActive && !isCompleted ? [0, -10, 10, -5, 0] : 0,
          scale: isActive && !isCompleted ? [1, 1.15, 1] : 1,
          boxShadow: isActive
            ? `0 0 24px ${accent}66, inset 0 0 14px ${accent}33`
            : "none",
        }}
        transition={{ duration: 0.7, ease: "easeOut" }}
        style={{
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: isActive ? accent : "rgba(255,255,255,0.12)",
        }}
      >
        <Icon
          className="w-6 h-6"
          style={{
            color: isActive ? accent : "rgba(255,255,255,0.3)",
            filter: isActive ? `drop-shadow(0 0 6px ${accent})` : "none",
            transition: "color 0.5s, filter 0.5s",
          }}
          strokeWidth={2.2}
        />
      </motion.div>

      <h3 className="font-display text-xl font-bold text-white relative z-10">
        {step.title}
      </h3>
      <p className="mt-3 text-sm leading-relaxed text-zinc-400 relative z-10 flex-1">
        {step.text}
      </p>

      {/* Step 4 — multi-stage celebration */}
      {isLast && <Celebration show={showConfetti} accent={accent} />}

      {/* Step 4 — success badge with shimmer */}
      <AnimatePresence>
        {isLast && showConfetti && (
          <motion.div
            initial={{ opacity: 0, scale: 0.4, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{
              type: "spring",
              stiffness: 280,
              damping: 14,
              delay: 0.25,
            }}
            className="mt-5 inline-flex items-center gap-1.5 text-white text-xs font-black uppercase tracking-widest px-3.5 py-2 self-start relative z-30 overflow-hidden"
            style={{
              background: `linear-gradient(90deg, ${accent}, #ef4444, ${accent})`,
              backgroundSize: "200% 100%",
              boxShadow: `0 0 26px ${accent}cc`,
            }}
          >
            <motion.span
              className="absolute inset-0"
              style={{
                background: `linear-gradient(90deg, ${accent}, #ef4444, ${accent})`,
                backgroundSize: "200% 100%",
              }}
              animate={{ backgroundPosition: ["0% 0%", "200% 0%"] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "linear" }}
            />
            <PartyPopper className="w-3.5 h-3.5 relative z-10" />
            <span className="relative z-10">{confettiBadgeLabel}</span>
            <SparklesIcon className="w-3.5 h-3.5 relative z-10" />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function Process({
  steps = DEFAULT_STEPS,
  eyebrow = "04 — Process",
  tagline,
  title,
  subtitle = "A repeatable 4-step engine that takes any app from unknown to category leader — across iOS, Android and 15+ markets.",
  confettiBadgeLabel = "Growth Unlocked",
  sectionId = "process",
  lightAll = false,
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-120px" });
  const [activeStep, setActiveStep] = useState(lightAll ? steps.length - 1 : -1);
  const [arrowToStep, setArrowToStep] = useState(lightAll ? steps.length - 1 : -1);
  const [showConfetti, setShowConfetti] = useState(false);

  const stepCount = steps.length;
  const lastIdx = stepCount - 1;
  const taglineText = tagline || `${stepCount}-step engine`;

  // Build dynamic timing: each step activates ~700ms apart, arrow appears 900ms before next step
  useEffect(() => {
    if (!inView) return;
    if (lightAll) {
      setActiveStep(lastIdx);
      setArrowToStep(lastIdx);
      const t = setTimeout(() => setShowConfetti(true), 600);
      return () => clearTimeout(t);
    }
    const timers = [];
    const stepGap = 1600;
    const arrowOffset = 900;
    for (let i = 0; i < stepCount; i++) {
      const stepTime = 300 + i * stepGap;
      if (i > 0) {
        timers.push(setTimeout(() => setArrowToStep(i), stepTime - arrowOffset + 200));
      }
      timers.push(
        setTimeout(() => {
          setActiveStep(i);
          if (i === lastIdx) {
            timers.push(setTimeout(() => setShowConfetti(true), 250));
          }
        }, stepTime)
      );
    }
    return () => timers.forEach(clearTimeout);
  }, [inView, stepCount, lastIdx]);

  // Build grid template for any step count: 1fr 72px 1fr 72px ... 1fr
  const gridTemplate = Array.from({ length: 2 * stepCount - 1 }, (_, i) =>
    i % 2 === 0 ? "1fr" : "72px"
  ).join(" ");

  // Default title node if not passed
  const titleNode = title ?? (
    <>
      Our App Marketing Process —{" "}
      <span className="relative inline-block">
        <span className="relative z-10">From Audit to Growth</span>
        <span className="absolute inset-x-0 bottom-1 h-3 bg-electric/30 -z-0" />
      </span>
    </>
  );

  return (
    <section
      id={sectionId}
      className="py-20 md:py-28 bg-charcoal text-white relative overflow-hidden"
      data-testid="process-section"
    >
      <div className="absolute inset-0 bg-grid-pattern-dark opacity-40 pointer-events-none" />
      <BackgroundOrbs />

      <div className="container relative" ref={ref}>
        {/* Header with progress indicator */}
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <p className="text-overline text-electric">{eyebrow}</p>
              <span className="w-12 h-px bg-electric/50" />
              <span className="text-xs uppercase tracking-widest text-zinc-500">
                {taglineText}
              </span>
            </div>
            <h2 className="font-display text-4xl md:text-5xl lg:text-[3.5rem] font-extrabold text-balance leading-[1.05]">
              {titleNode}
            </h2>
          </div>
          <div className="lg:col-span-4">
            <ProcessStairs steps={steps} activeStep={activeStep} />
            <p className="mt-2 text-zinc-300 leading-relaxed">{subtitle}</p>
          </div>
        </div>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-2 mb-10">
          {steps.map((s, i) => (
            <motion.div
              key={i}
              className="h-1 rounded-full"
              animate={{
                width: activeStep >= i ? 56 : 24,
                backgroundColor: activeStep >= i ? s.accent : "rgba(255,255,255,0.15)",
                boxShadow: activeStep >= i ? `0 0 12px ${s.accent}aa` : "none",
              }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            />
          ))}
        </div>

        {/* Steps grid — N cards interleaved with N-1 connector slots */}
        <div
          className="grid grid-cols-1 items-stretch lg:[grid-template-columns:var(--cols)]"
          style={{ "--cols": gridTemplate }}
        >
          {steps.map((step, i) => (
            <Fragment key={step.num}>
              <StepCard
                step={step}
                idx={i}
                isActive={lightAll || activeStep >= i}
                isCompleted={!lightAll && activeStep > i && i < lastIdx}
                isLast={i === lastIdx}
                showConfetti={i === lastIdx && showConfetti}
                confettiBadgeLabel={confettiBadgeLabel}
              />
              {i < lastIdx && (
                <>
                  <ArrowConnector
                    animate={arrowToStep >= i + 1}
                    direction="horizontal"
                    accentFrom={step.accent}
                    accentTo={steps[i + 1].accent}
                  />
                  <ArrowConnector
                    animate={activeStep >= i}
                    direction="vertical"
                    accentFrom={step.accent}
                    accentTo={steps[i + 1].accent}
                  />
                </>
              )}
            </Fragment>
          ))}
        </div>
      </div>
    </section>
  );
}
